import React, { useState } from 'react'
import { Typography, Grid, Card, CardContent, useTheme, Box, IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { tokens } from "../../../assets/theme";
import { ResponsiveLine } from '@nivo/line'
import { format } from 'date-fns';
import MachineGantt from "./machineGantt";
import messages from "./messages";
import { FormattedMessage } from "react-intl";

const processData = (data, thresholds, fromDate, toDate) => {
    const groupedData = {};
    const colorMap = {};
    const minValueMap = {};
    const maxValueMap = {};
    const descriptionMap = {};
    const nameMap = {};
    const readingTypeIdMap = {};
    const thresholdMinMap = {};
    const thresholdMaxMap = {};

    data.forEach(({ dataType, unit, timestampLocalTime, value, unitColor, name, description, readingTypeId }) => {
        const key = `${dataType}`;
        if (!groupedData[key]) {
            groupedData[key] = [];
            colorMap[key] = unitColor; // Store color for each data type
            minValueMap[key] = value;
            maxValueMap[key] = value;
            nameMap[key] = name;
            descriptionMap[key] = description;
            readingTypeIdMap[key] = readingTypeId;
            thresholdMinMap[key] = thresholds.find(x => x.readingTypeId === readingTypeId && x.enableLowLimit === true)?.lowLimitValue;
            thresholdMaxMap[key] = thresholds.find(x => x.readingTypeId === readingTypeId && x.enableHighLimit === true)?.highLimitValue;
        }
        groupedData[key].push({ x: new Date(timestampLocalTime), y: value });
        minValueMap[key] = Math.min(minValueMap[key], value);
        maxValueMap[key] = Math.max(maxValueMap[key], value);
    });

    return Object.keys(groupedData).map((key) => ({
        id: key,
        name: nameMap[key],
        description: descriptionMap[key],
        color: colorMap[key],
        minValue: thresholdMinMap[key] !== undefined ? Math.min(minValueMap[key], thresholdMinMap[key]) : minValueMap[key],
        maxValue: thresholdMaxMap[key] !== undefined ? Math.max(maxValueMap[key], thresholdMaxMap[key]) : maxValueMap[key],
        data: { id: key, data: groupedData[key].sort((a, b) => a.x - b.x) }, 
        minTheshold: thresholdMinMap[key] !== undefined ? { id: "min-threshold", data: [ { x: new Date(fromDate), y: thresholdMinMap[key] }, { x: new Date(toDate), y: thresholdMinMap[key] } ] } : { id: "min-threshold", data: [] },
        maxTheshold: thresholdMaxMap[key] !== undefined ? { id: "max-threshold", data: [ { x: new Date(fromDate), y: thresholdMaxMap[key] }, { x: new Date(toDate), y: thresholdMaxMap[key] } ] } : { id: "max-threshold", data: [] }
        }));
};

const CustomTooltip = ({ point }) => (
    <div
        style={{
            background: '#333333',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            color: '#ffffff',
        }}
    >
        {format(new Date(point.data.x), 'yyyy-MM-dd HH:mm')}
        <br />
        {point.data.y}
    </div>
);

export default function SensorDataDiagram(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const nrOfDataPoints = props.sensorDataQuery.data?.length ?? 0;
    const chartData = processData(props.sensorDataQuery.data, props.thresholds, props.fromDate, props.toDate);
    const [minDate, setMinDate] = useState(props.fromDate);
    const [maxDate, setMaxDate] = useState(props.toDate);
    const [collapsed, setCollapsed] = useState({});
    const toggleCollapse = (key) => {
        setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const calculateMinutesBetween = (minDate, maxDate) => {
        const diffMs = new Date(maxDate) - new Date(minDate);
        return Math.floor(diffMs / 60000); // Convert milliseconds to minutes
    };

    const setXAxisResolution = () => {
        var minutes = calculateMinutesBetween(props.fromDate, props.toDate);
        return minutes < 250 ? "every 15 minute" :
            minutes < 500 ? "every 30 minute" :
                minutes < 1400 ? "every 1 hour" :
                    minutes < 2800 ? "every 2 hour" :
                        minutes < 4000 ? "every 3 hour" :
                            minutes < 6000 ? "every 4 hour" :
                                "every 12 hour";
    }

    return (
        <>
            { (chartData === undefined || chartData.length === 0) && 
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                        <FormattedMessage {...messages.noSensorData} />
                    </Box> 
                </Grid>
            }           
            {chartData.map((chart) => (
                <Grid item xs={12} key={chart.id}>
                    <Card sx={{ backgroundColor: "#2e2e3e" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="h5" sx={{ mb: 1, pl: 3 }}>{`${chart.name} ${chart.description !== undefined && chart.description !== "" ? " - " : ""} ${chart.description}`}</Typography>
                                <IconButton sx={{ backgroundColor: colors.primary[600] }} onClick={() => toggleCollapse(chart.id)} color="inherit">
                                    {collapsed[chart.id] ? <ExpandMore /> : <ExpandLess />}
                                </IconButton>
                            </Box>
                            {!collapsed[chart.id] && (
                                <Box sx={{ height: 300 }}>
                                    <ResponsiveLine
                                        data={[chart.data]}
                                        tooltip={CustomTooltip}
                                        theme={{
                                            axis: {
                                                ticks: {
                                                    text: {
                                                        fill: '#cccccc',
                                                    },
                                                },
                                                legend: {
                                                    text: {
                                                        fill: '#cccccc',
                                                    },
                                                },
                                            },
                                            legends: {
                                                text: {
                                                    fill: '#cccccc',
                                                },
                                            },
                                        }}
                                        margin={{ top: 20, right: 50, bottom: 50, left: 50 }}
                                        xScale={{
                                            type: "time",
                                            format: "%Y-%m-%d %H:%M:%S",
                                            precision: "minute",
                                            min: new Date(props.fromDate),
                                            max: new Date(props.toDate)
                                        }}
                                        yScale={{ type: "linear", min: chart.minValue * 0.95, max: chart.maxValue * 1.05, stacked: false, reverse: false }}
                                        axisBottom={null}
                                        axisLeft={{
                                            tickSize: 1,
                                            tickPadding: 5,
                                            tickRotation: 0,
                                            enable: true,
                                        }}
                                        axisTop={{
                                            orient: 'top',
                                            format: "%m-%d %H:%M",
                                            formatType: "time",
                                            tickValues: setXAxisResolution(),
                                            tickRotation: 0,
                                            legend: '',
                                            legendOffset: 10,
                                            truncateTickAt: 0
                                        }}
                                        colors={[chart.color, "#ffffff", "#ffffff"]}
                                        colorBy="id"
                                        //curve="cardinal"
                                        curve="step"
                                        //curve="basis"
                                        lineWidth={2}
                                        enablePoints={false}
                                        useMesh={true}
                                        enableGridY={false}
                                        enableGridX={false}
                                        enableArea={true}
                                        areaOpacity={0.2}
                                        areaBaselineValue={chart.minValue * 0.95}
                                        enableTouchCrosshair={true}
                                        defs={[
                                            {
                                                id: 'area-off',
                                                type: 'linearGradient',
                                                colors: [
                                                    { offset: 50, color: 'rgba(0, 0, 0, 0)' },
                                                    { offset: 50, color: 'rgba(0, 0, 0, 0)' },
                                                ],
                                            }
                                        ]}
                                        fill={[
                                            {
                                                match: {
                                                    id: 'min-threshold', 
                                                },
                                                id: 'area-off',
                                            },
                                            {
                                                match: {
                                                    id: 'max-threshold', 
                                                },
                                                id: 'area-off',
                                            },
                                        ]}
                                        layers={[
                                            'grid',
                                            'markers',
                                            'axes',
                                            'areas',
                                            'lines',
                                            'crosshair',
                                            'points',
                                            'slices',
                                            'mesh',
                                            ({ lineGenerator, xScale, yScale }) => (
                                                <g>
                                                    {[chart.minTheshold, chart.maxTheshold].map(series => (
                                                        <path
                                                            key={series.id}
                                                            d={lineGenerator(
                                                                series.data.map(d => ({
                                                                    x: xScale(d.x),
                                                                    y: yScale(d.y),
                                                                }))
                                                            )}
                                                            fill="none"
                                                            stroke="#ffffff"
                                                            strokeWidth={1}
                                                            strokeDasharray="8 10"
                                                        />
                                                    ))}
                                                </g>
                                            ),
                                        ]}
                                    />
                                </Box>
                            )}
                            {!collapsed[chart.id] && (
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"  }}>
                                    <MachineGantt 
                                        sx={{ ml: 4 }} 
                                        data={props.ganttQueryData} 
                                        startDate={props.ganttStartDate} 
                                        endDate={props.ganttEndDate} 
                                        company={1} 
                                        chartContainer={`chart-${chart.id}`} 
                                        featureFlags={props.featureFlags} 
                                        divClass="chart-minigantt"
                                        showHeader={false}
                                    />
                                </Box>
                            )}

                        </CardContent>
                    </Card>

                </Grid>

            ))}
        </>

    )

}